import { useState } from 'react';
import { usePwaInstall } from '../../pwa/install.ts';
import './InstallStep.css';

/**
 * The "add to home screen" page of the first-run tour. Rendered inside the
 * AgentIntro card, so it shares that card's chrome (the agent header, the step
 * dots, and the Skip / Enter footer live in AgentIntro).
 *
 * Two paths:
 *   - Chromium/Android: a captured `beforeinstallprompt` lets the big button
 *     fire the real install dialog.
 *   - iOS Safari: no programmatic install exists, so we show the Share ->
 *     "Add to Home Screen" instruction instead of a button.
 *
 * The step is only ever mounted when one of those paths is available (AgentIntro
 * gates on canOfferInstall()), so there is no inert third state to design for.
 */

type Status = 'idle' | 'installing' | 'installed' | 'dismissed';

function ShareGlyph() {
  // iOS share icon: a box with an up-arrow, used to point at the toolbar button.
  return (
    <svg
      className="om-install__glyph"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 15V3" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    </svg>
  );
}

export default function InstallStep({ agentName }: { agentName: string }) {
  const { canPrompt, isIos, promptInstall } = usePwaInstall();
  const [status, setStatus] = useState<Status>('idle');

  async function handleInstall() {
    setStatus('installing');
    const outcome = await promptInstall();
    setStatus(outcome === 'accepted' ? 'installed' : 'dismissed');
  }

  return (
    <div className="om-install">
      <span className="om-install__tag">INSTALL</span>
      <h2 className="om-install__title">Keep {agentName} one tap away</h2>
      <p className="om-install__body">
        Add OculusMind to your home screen. It opens full-screen — no browser bar — ready the moment
        you need an agent.
      </p>

      {isIos ? (
        <div className="om-install__ios" role="note">
          <p className="om-install__ios-lead">To install on iPhone or iPad:</p>
          <ol className="om-install__steps">
            <li>
              Tap the Share icon <ShareGlyph /> in the toolbar
            </li>
            <li>
              Choose <strong>Add to Home Screen</strong>
            </li>
          </ol>
        </div>
      ) : status === 'installed' ? (
        <p className="om-install__done" role="status">
          Installed — find OculusMind on your home screen.
        </p>
      ) : (
        <>
          <button
            type="button"
            className="om-install__cta"
            onClick={() => {
              void handleInstall();
            }}
            disabled={!canPrompt || status === 'installing'}
          >
            {status === 'installing' ? 'Opening…' : 'Add to Home Screen'}
          </button>
          {status === 'dismissed' ? (
            <p className="om-install__hint" role="status">
              No problem — you can install any time from your browser menu.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
